/**
 * Wheel Share Video — attribution pure logic (C10, SPEC §6.5 + §7).
 *
 * The deterministic, framework-free core for the video's attribution: WHO made
 * the Wheel (creator credit) and, when a credit-required music track is chosen,
 * the license's exact required music credit. No React / network / DOM here — so
 * it is exhaustively unit-testable and the display component (Attribution.jsx)
 * and the render-payload builder (shareVideo.buildRequestPayload) both consume
 * the SAME normalized model. This is the FE DISPLAY + the attribution DATA that
 * a later render (C11) bakes into the end-card.
 *
 * Two hard rules baked in here (one place, tested):
 *  1. PRIVACY (EW-323 / server owner_privacy.redact_owner_pii): the owner's real
 *     name is public creator credit ONLY. A non-public Wheel must never surface
 *     it — so `show` is false unless `isPublic` is true. Fail-closed: an unknown
 *     `isPublic` is treated as NOT public.
 *  2. MUSIC CREDIT IS VERBATIM (SPEC §6.5): the credit text is data on the track
 *     record (`attribution_text`) and is used exactly, never paraphrased. A track
 *     that requires no credit (CC0 / no attribution_text) shows no music line.
 *     Narration and Portrait audio are the user's own content → never credited.
 */

// Display-only truncation caps. The PAYLOAD always carries the full, untruncated
// value (the render bakes the real credit); only the on-screen line is clipped
// so a very long handle/name can't blow out the modal layout.
export const MAX_HANDLE_DISPLAY = 24;
export const MAX_NAME_DISPLAY = 40;
export const MAX_MUSIC_DISPLAY = 90;

/**
 * Clip a string to `max` characters, appending a single ellipsis when clipped.
 * Returns "" for nullish/blank input. Pure and total (never throws).
 */
export function truncateText(value, max) {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  if (!s) return "";
  if (!Number.isFinite(max) || max <= 0 || s.length <= max) return s;
  // Reserve one slot for the ellipsis so the result is exactly `max` chars.
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

/** Normalize a handle: strip a single leading "@" and surrounding whitespace. */
export function normalizeHandle(handle) {
  if (handle === null || handle === undefined) return "";
  return String(handle).trim().replace(/^@+/, "").trim();
}

/**
 * Build the creator-attribution model from the Wheel owner's data.
 *
 * @param {object} creator
 * @param {string} [creator.firstName]
 * @param {string} [creator.lastName]
 * @param {string} [creator.handle]   an @username, when one exists (none today —
 *                                    Wheel.user_profile has no username field —
 *                                    but supported so a future handle "just works")
 * @param {boolean} [creator.isPublic] whether the Wheel is public (EW-323 gate)
 * @returns {{ show:boolean, name:string, handle:string, displayName:string,
 *             displayHandle:string, line:string }}
 */
export function buildCreatorAttribution(creator = {}) {
  const { firstName, lastName, handle, isPublic } = creator || {};
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  const normHandle = normalizeHandle(handle);
  // Fail-closed on privacy: only an explicit public Wheel may show the credit.
  const isPub = isPublic === true;
  const hasIdentity = Boolean(name || normHandle);
  const show = isPub && hasIdentity;

  const displayHandle = normHandle
    ? `@${truncateText(normHandle, MAX_HANDLE_DISPLAY - 1)}`
    : "";
  const displayName = truncateText(name, MAX_NAME_DISPLAY);
  // Prefer a handle for the one-line credit; fall back to the display name.
  const credited = displayHandle || displayName;

  return {
    show,
    name,
    handle: normHandle,
    displayName,
    displayHandle,
    line: show && credited ? `Created by ${credited}` : "",
  };
}

/**
 * True when a music track requires a credit. A track requires credit iff it
 * carries a non-empty `attribution_text` (SPEC §6.5: the credit text is data on
 * the track record). CC0 / public-domain tracks carry no attribution_text, so
 * they require no credit. An explicit `requires_credit === false` also opts out.
 */
export function musicRequiresCredit(track) {
  if (!track) return false;
  if (track.requires_credit === false) return false;
  const text = track.attribution_text;
  return typeof text === "string" && text.trim().length > 0;
}

/**
 * Build the music-credit model for a selected track (or null when there is no
 * track / no credit required). The credit `text` is the track's attribution_text
 * VERBATIM. `line` is the on-screen (truncated) display string.
 *
 * @param {object|null} track { id?, track_id?, title?, artist?, attribution_text?, license? }
 */
export function buildMusicCredit(track) {
  if (!musicRequiresCredit(track)) {
    return { show: false, trackId: null, title: "", artist: "", text: "", line: "" };
  }
  const text = String(track.attribution_text).trim();
  const trackId = track.track_id ?? track.id ?? null;
  return {
    show: true,
    trackId,
    title: track.title ? String(track.title).trim() : "",
    artist: track.artist ? String(track.artist).trim() : "",
    text,
    line: `Music: ${truncateText(text, MAX_MUSIC_DISPLAY)}`,
  };
}

/**
 * Combine creator + music into one attribution model the UI renders around.
 * `hasAny` is true when at least one credit will actually show — the modal
 * renders no attribution section at all when it is false (missing-data guard).
 */
export function buildAttribution({ creator, musicTrack } = {}) {
  const creatorModel = buildCreatorAttribution(creator);
  const musicModel = buildMusicCredit(musicTrack);
  return {
    creator: creatorModel,
    music: musicModel,
    hasAny: creatorModel.show || musicModel.show,
  };
}

/**
 * Assemble the attribution sub-object for the render POST body so a later render
 * (C11) can bake the end-card credit. Emits ONLY the parts that will show —
 * respecting the privacy gate (no creator block for a private Wheel) and the
 * no-credit-required rule (no music block for CC0). Returns `undefined` when
 * there is nothing to bake, so the payload stays byte-identical to today for the
 * common case (no attribution to carry).
 *
 * Snake_case keys to match the DRF backend's config convention (SPEC §8.2).
 */
export function attributionToPayload(model) {
  if (!model || !model.hasAny) return undefined;
  const payload = {};
  if (model.creator && model.creator.show) {
    const creator = {};
    if (model.creator.name) creator.name = model.creator.name;
    if (model.creator.handle) creator.handle = model.creator.handle;
    payload.creator = creator;
  }
  if (model.music && model.music.show) {
    const music = { attribution_text: model.music.text };
    if (model.music.trackId != null) music.track_id = model.music.trackId;
    if (model.music.title) music.title = model.music.title;
    if (model.music.artist) music.artist = model.music.artist;
    payload.music = music;
  }
  return Object.keys(payload).length > 0 ? payload : undefined;
}

/**
 * Convenience: go straight from raw inputs to the render-payload sub-object.
 * Used by buildRequestPayload so callers pass `{ creator, musicTrack }` once.
 */
export function buildAttributionPayload(input) {
  return attributionToPayload(buildAttribution(input || {}));
}
